import {
    ButtonProps,
    HStack,
    VStack,
    Box,
    Button,
    IconButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useEffect } from 'react';
import { partition } from 'lodash';
import { ColumnDefinitionEnum } from '@mark43/cad-api';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useQueueStore } from '../../queues/context/use-queue-store';
import { UnitQueueStore } from '../store/unit-queue-store';
import { QueueFilterMultiselect } from '../../common/components/queues-filter-select';
import {
    AgencyTypeGlobal,
    agencyTypeIdToDisplay,
    globalAttributes,
} from '../../common/global-attributes';
import { useUnitQueueFilterOptions } from './get-unit-queue-filter-options';
import { entityIdsToFilterOptions } from '../../queues/queue-filter-helpers';
import { useIsAgencyTypeGlobalAttributeIdConsidered } from '../../common/hooks/use-is-agency-type-global-attribute-id-considered';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';

export const UnitTypeFilterPopover = observer(function UnitTypeFilterPopover({
    enabledFilters,
    buttonVariant = 'ghost',
}: {
    enabledFilters?: string[];
    buttonVariant?: ButtonProps['variant'];
}): React.ReactElement {
    const { queuesRearchitectureEnabled } = useEnabledFeatures();
    const { isCad, isFirstResponder } = useApplicationType();
    const { userAgencyTypeGlobalAttrId } = useBootstrapState();
    const preferUnitQueueColumnFiltersForUnitType = queuesRearchitectureEnabled || isFirstResponder;

    const queueStore = useQueueStore() as UnitQueueStore;

    const {
        additionalCfsOptions,
        cfsOptions,
        priorityOptions,
        alarmLevelOptions,
        eventLabelOptions,
        dispatchAreaOptions,
        unitDispatchAreaOptions,
        agencyOptions,
        stationOptions,
        unitStateOptions,
        patrolTypeOptions,
        fireUnitTypeOptions,
        emsUnitTypeOptions,
        policeUnitTypeOptions,
        equipmentTypeOptions,
        skillsTypeOptions,
        unitPrimarySubdivisionOptions,
        eventPrimarySubdivisionOptions,
        availabilityOptions,
    } = useUnitQueueFilterOptions();

    const isQueueType = useIsAgencyTypeGlobalAttributeIdConsidered();

    const isUserPolicyAgency =
        userAgencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.POLICE;

    useEffect(() => {
        if (!isFirstResponder) return;
        const queueClasses: ('FIRE' | 'MEDICAL' | 'POLICE')[] = isUserPolicyAgency
            ? ['POLICE', 'FIRE', 'MEDICAL']
            : ['FIRE', 'MEDICAL'];
        queueStore.setQueueClasses(queueClasses);
    }, [isFirstResponder, isUserPolicyAgency, queueStore]);

    const allSelectedUnitQueueColumnFilterValues: number[] = (
        queueStore.workspacePanel.queue.unitQueueColumnFilters ?? []
    )
        .filter(
            (unitQueueFilter) =>
                unitQueueFilter.columnDefinition !== ColumnDefinitionEnum.ADDITIONAL_CFS.name &&
                unitQueueFilter.columnDefinition !== ColumnDefinitionEnum.BEAT.name &&
                unitQueueFilter.columnDefinition !== ColumnDefinitionEnum.DISPATCH_AREA.name &&
                unitQueueFilter.columnDefinition !== ColumnDefinitionEnum.UNIT_DISPATCH_AREA.name
        )
        .map((unitQueueFilter) => unitQueueFilter.entityId);

    const allSelectedEventDispatchAreaFilterValues: number[] = (
        queueStore.workspacePanel.queue.unitQueueColumnFilters ?? []
    )
        .filter(
            (unitQueueFilter) =>
                unitQueueFilter.columnDefinition === ColumnDefinitionEnum.DISPATCH_AREA.name
        )
        .map((unitQueueFilter) => unitQueueFilter.entityId);

    const allSelectedUnitDispatchAreaFilterValues: number[] = (
        queueStore.workspacePanel.queue.unitQueueColumnFilters ?? []
    )
        .filter(
            (unitQueueFilter) =>
                unitQueueFilter.columnDefinition === ColumnDefinitionEnum.UNIT_DISPATCH_AREA.name
        )
        .map((unitQueueFilter) => unitQueueFilter.entityId);

    const allSelectedAdditionalCfsFilterValues: number[] = (
        queueStore.workspacePanel.queue.unitQueueColumnFilters ?? []
    )
        .filter(
            (unitQueueFilter) =>
                unitQueueFilter.columnDefinition === ColumnDefinitionEnum.ADDITIONAL_CFS.name
        )
        .map((unitQueueFilter) => unitQueueFilter.entityId);

    const allSelectedEventPrimarySubdivFilterValues: number[] = (
        queueStore.workspacePanel.queue.unitQueueColumnFilters ?? []
    )
        .filter(
            (unitQueueFilter) => unitQueueFilter.columnDefinition === ColumnDefinitionEnum.BEAT.name
        )
        .map((unitQueueFilter) => unitQueueFilter.entityId);

    const unitQueueColumnFiltersUnitTypePartition = useMemo(
        () =>
            partition(
                queueStore.workspacePanel.queue.unitQueueColumnFilters ?? [],
                (columnFilter) =>
                    columnFilter.columnDefinition === ColumnDefinitionEnum.UNIT_TYPE.name
            ),
        [queueStore.workspacePanel.queue.unitQueueColumnFilters]
    );

    const allSelectedUnitTypeFilterValues = useMemo(() => {
        let unitTypeValues: number[] = [];
        const unitTypeFilterValuesFromUnitQueueColumnFilters: number[] = unitQueueColumnFiltersUnitTypePartition[0].map(
            (unitQueueFilter) => unitQueueFilter.entityId
        );
        const unitTypeFilterValuesFromUnitTypeFilters: number[] = (
            queueStore.workspacePanel.queue.unitTypeFilters ?? []
        ).map((unitTypeFilter) => unitTypeFilter.attributeId);

        // when queuesRearchitectureEnabled = true, unit types are stored in unitQueueColumnFilters instead of unitTypeFilters.
        // for users who load queues for the first time after this app setting enabled, the unit types are empty in unitQueueColumnFilters,
        // in this case, grab the filter from unitTypeFilters, if there are any.
        if (preferUnitQueueColumnFiltersForUnitType) {
            unitTypeValues = unitTypeFilterValuesFromUnitQueueColumnFilters.length
                ? unitTypeFilterValuesFromUnitQueueColumnFilters
                : unitTypeFilterValuesFromUnitTypeFilters;
        } else {
            unitTypeValues = unitTypeFilterValuesFromUnitTypeFilters.length
                ? unitTypeFilterValuesFromUnitTypeFilters
                : unitTypeFilterValuesFromUnitQueueColumnFilters;
        }
        return unitTypeValues;
    }, [
        preferUnitQueueColumnFiltersForUnitType,
        unitQueueColumnFiltersUnitTypePartition,
        queueStore.workspacePanel.queue.unitTypeFilters,
    ]);

    const memoizedOptions = useMemo(() => {
        return [
            ...agencyOptions,
            ...alarmLevelOptions,
            ...cfsOptions,
            ...eventLabelOptions,
            ...priorityOptions,
            ...stationOptions,
            ...unitStateOptions,
            ...equipmentTypeOptions,
            ...skillsTypeOptions,
            ...patrolTypeOptions,
            ...unitPrimarySubdivisionOptions,
            ...availabilityOptions,
            ...policeUnitTypeOptions,
            ...fireUnitTypeOptions,
            ...emsUnitTypeOptions,
        ];
    }, [
        agencyOptions,
        alarmLevelOptions,
        cfsOptions,
        eventLabelOptions,
        priorityOptions,
        stationOptions,
        unitStateOptions,
        equipmentTypeOptions,
        skillsTypeOptions,
        patrolTypeOptions,
        availabilityOptions,
        unitPrimarySubdivisionOptions,
        policeUnitTypeOptions,
        fireUnitTypeOptions,
        emsUnitTypeOptions,
    ]);

    const memoizedAdditionalCfsOptions = useMemo(() => {
        return additionalCfsOptions;
    }, [additionalCfsOptions]);

    const memoizedEventPrimarySubdivisionOptions = useMemo(() => {
        return eventPrimarySubdivisionOptions;
    }, [eventPrimarySubdivisionOptions]);

    const memoizedDispatchAreaOptions = useMemo(() => {
        return dispatchAreaOptions;
    }, [dispatchAreaOptions]);

    const memoizedUnitDispatchAreaOptions = useMemo(() => {
        return unitDispatchAreaOptions;
    }, [unitDispatchAreaOptions]);

    const onChangeUnitQueueColumnFilters = useCallback(
        (entityIds: number[]) => {
            queueStore.setUnitQueueColumnFilters(
                entityIdsToFilterOptions(memoizedOptions, entityIds)
            );
        },
        [queueStore, memoizedOptions]
    );

    const onChangeAdditionalCfsFilters = useCallback(
        (entityIds: number[]) => {
            queueStore.upsertUnitQueueFilters(
                entityIdsToFilterOptions(
                    memoizedAdditionalCfsOptions,
                    entityIds,
                    ColumnDefinitionEnum.ADDITIONAL_CFS.name
                ),
                'ADDITIONAL_CFS'
            );
        },
        [queueStore, memoizedAdditionalCfsOptions]
    );

    const onChangeEventPrimarySubdivFilters = useCallback(
        (entityIds: number[]) => {
            queueStore.upsertUnitQueueFilters(
                entityIdsToFilterOptions(
                    memoizedEventPrimarySubdivisionOptions,
                    entityIds,
                    ColumnDefinitionEnum.BEAT.name
                ),
                'BEAT'
            );
        },
        [queueStore, memoizedEventPrimarySubdivisionOptions]
    );

    const onChangeEventDispatchAreaFilters = useCallback(
        (entityIds: number[]) => {
            queueStore.upsertUnitQueueFilters(
                entityIdsToFilterOptions(
                    memoizedDispatchAreaOptions,
                    entityIds,
                    ColumnDefinitionEnum.DISPATCH_AREA.name
                ),
                'DISPATCH_AREA'
            );
        },
        [queueStore, memoizedDispatchAreaOptions]
    );

    const onChangeUnitDispatchAreaFilters = useCallback(
        (entityIds: number[]) => {
            queueStore.upsertUnitQueueFilters(
                entityIdsToFilterOptions(
                    memoizedUnitDispatchAreaOptions,
                    entityIds,
                    ColumnDefinitionEnum.UNIT_DISPATCH_AREA.name
                ),
                'UNIT_DISPATCH_AREA'
            );
        },
        [queueStore, memoizedUnitDispatchAreaOptions]
    );

    const onChangeUnitTypeFilters = useCallback(
        (attrIds: number[]) => {
            queueStore.setUnitTypeAttrIds(attrIds);
        },
        [queueStore]
    );

    const totalNumberOfFiltersSelected = useMemo(
        () =>
            allSelectedAdditionalCfsFilterValues.length +
            allSelectedUnitQueueColumnFilterValues.length +
            allSelectedEventPrimarySubdivFilterValues.length +
            allSelectedEventDispatchAreaFilterValues.length +
            allSelectedUnitDispatchAreaFilterValues.length +
            allSelectedUnitTypeFilterValues.length,
        [
            allSelectedAdditionalCfsFilterValues,
            allSelectedUnitQueueColumnFilterValues,
            allSelectedEventPrimarySubdivFilterValues,
            allSelectedEventDispatchAreaFilterValues,
            allSelectedUnitDispatchAreaFilterValues,
            allSelectedUnitTypeFilterValues,
        ]
    );

    const showClearAll = useMemo(() => totalNumberOfFiltersSelected > 0, [
        totalNumberOfFiltersSelected,
    ]);

    const clearAll = useCallback(() => {
        onChangeUnitQueueColumnFilters([]);
        onChangeAdditionalCfsFilters([]);
        onChangeEventPrimarySubdivFilters([]);
        onChangeEventDispatchAreaFilters([]);
        onChangeUnitDispatchAreaFilters([]);
        onChangeUnitTypeFilters([]);
    }, [
        onChangeUnitQueueColumnFilters,
        onChangeAdditionalCfsFilters,
        onChangeEventPrimarySubdivFilters,
        onChangeEventDispatchAreaFilters,
        onChangeUnitDispatchAreaFilters,
        onChangeUnitTypeFilters,
    ]);

    const isActive =
        !!queueStore.workspacePanel.queue.unitTypeFilters?.length ||
        !!queueStore.workspacePanel.queue.unitQueueColumnFilters?.length;

    const filterComponentsMap = useMemo(() => {
        let components = [
            {
                label: ColumnDefinitionEnum.ADDITIONAL_CFS.displayName,
                attributeOptions: additionalCfsOptions,
                values: allSelectedAdditionalCfsFilterValues,
                onChange: onChangeAdditionalCfsFilters,
            },
            {
                label: ColumnDefinitionEnum.ALARM_LEVEL.displayName,
                attributeOptions: alarmLevelOptions,
                values: allSelectedUnitQueueColumnFilterValues,
                onChange: onChangeUnitQueueColumnFilters,
            },
            {
                label: ColumnDefinitionEnum.CFS_NUMBER_AND_TEXT.displayName,
                attributeOptions: cfsOptions,
                values: allSelectedUnitQueueColumnFilterValues,
                onChange: onChangeUnitQueueColumnFilters,
            },
            {
                label: ColumnDefinitionEnum.DISPATCH_AREA.displayName,
                attributeOptions: dispatchAreaOptions,
                values: allSelectedEventDispatchAreaFilterValues,
                onChange: onChangeEventDispatchAreaFilters,
            },
            {
                label: ColumnDefinitionEnum.UNIT_DISPATCH_AREA.displayName,
                attributeOptions: unitDispatchAreaOptions,
                values: allSelectedUnitDispatchAreaFilterValues,
                onChange: onChangeUnitDispatchAreaFilters,
            },
            {
                label: ColumnDefinitionEnum.UNIT_AGENCY.displayName,
                attributeOptions: agencyOptions,
                values: allSelectedUnitQueueColumnFilterValues,
                onChange: onChangeUnitQueueColumnFilters,
            },
            {
                label: ColumnDefinitionEnum.STATUS.displayName,
                attributeOptions: unitStateOptions,
                values: allSelectedUnitQueueColumnFilterValues,
                onChange: onChangeUnitQueueColumnFilters,
            },
            {
                label: ColumnDefinitionEnum.STATION.displayName,
                attributeOptions: stationOptions,
                values: allSelectedUnitQueueColumnFilterValues,
                onChange: onChangeUnitQueueColumnFilters,
            },
            {
                label: ColumnDefinitionEnum.EQUIPMENT.displayName,
                attributeOptions: equipmentTypeOptions,
                values: allSelectedUnitQueueColumnFilterValues,
                onChange: onChangeUnitQueueColumnFilters,
            },
            {
                label: ColumnDefinitionEnum.SKILLS.displayName,
                attributeOptions: skillsTypeOptions,
                values: allSelectedUnitQueueColumnFilterValues,
                onChange: onChangeUnitQueueColumnFilters,
            },
            {
                label: ColumnDefinitionEnum.PATROL_TYPE.displayName,
                attributeOptions: patrolTypeOptions,
                values: allSelectedUnitQueueColumnFilterValues,
                onChange: onChangeUnitQueueColumnFilters,
            },
            {
                label: ColumnDefinitionEnum.UNIT_BEAT.displayName,
                attributeOptions: unitPrimarySubdivisionOptions,
                values: allSelectedUnitQueueColumnFilterValues,
                onChange: onChangeUnitQueueColumnFilters,
            },
            {
                label: ColumnDefinitionEnum.BEAT.displayName,
                attributeOptions: eventPrimarySubdivisionOptions,
                values: allSelectedEventPrimarySubdivFilterValues,
                onChange: onChangeEventPrimarySubdivFilters,
            },
            {
                label: ColumnDefinitionEnum.AVAILABILITY.displayName,
                attributeOptions: availabilityOptions,
                values: allSelectedUnitQueueColumnFilterValues,
                onChange: onChangeUnitQueueColumnFilters,
            },
        ];

        if (enabledFilters?.length) {
            components = components.filter((component) => enabledFilters.includes(component.label));
        }

        if (isCad && !queuesRearchitectureEnabled) {
            components = components.filter(
                (component) =>
                    component.label !== ColumnDefinitionEnum.UNIT_DISPATCH_AREA.displayName
            );
        }

        return components.sort((a, b) => a.label.localeCompare(b.label));
    }, [
        additionalCfsOptions,
        allSelectedAdditionalCfsFilterValues,
        onChangeAdditionalCfsFilters,
        alarmLevelOptions,
        allSelectedUnitQueueColumnFilterValues,
        onChangeUnitQueueColumnFilters,
        cfsOptions,
        dispatchAreaOptions,
        allSelectedEventDispatchAreaFilterValues,
        unitDispatchAreaOptions,
        allSelectedUnitDispatchAreaFilterValues,
        agencyOptions,
        unitStateOptions,
        stationOptions,
        equipmentTypeOptions,
        skillsTypeOptions,
        patrolTypeOptions,
        unitPrimarySubdivisionOptions,
        eventPrimarySubdivisionOptions,
        allSelectedEventPrimarySubdivFilterValues,
        onChangeEventPrimarySubdivFilters,
        availabilityOptions,
        enabledFilters,
        onChangeEventDispatchAreaFilters,
        onChangeUnitDispatchAreaFilters,
        isCad,
        queuesRearchitectureEnabled,
    ]);

    const getUnitTypeOptions = (unitAgencyTypeGlobalAttrId: AgencyTypeGlobal) => {
        switch (unitAgencyTypeGlobalAttrId) {
            case globalAttributes.agencyTypeGlobal.FIRE:
                return fireUnitTypeOptions;
            case globalAttributes.agencyTypeGlobal.EMS:
                return emsUnitTypeOptions;
            default:
                return policeUnitTypeOptions;
        }
    };

    const getUnitTypePropsForType = (unitType: AgencyTypeGlobal) => {
        const allSelectedQueueColumnFilterValuesExceptUnitType = unitQueueColumnFiltersUnitTypePartition[1].map(
            (columnFilter) => columnFilter.entityId
        );
        const allValues = allSelectedQueueColumnFilterValuesExceptUnitType.concat(
            allSelectedUnitTypeFilterValues
        );

        return {
            label: agencyTypeIdToDisplay[unitType].capitalize + ' Unit Type',
            attributeOptions: getUnitTypeOptions(unitType),
            values: allValues,
            onChange: (updatedValues: (string | number)[]) => {
                onChangeUnitQueueColumnFilters(updatedValues as number[]);
                if (preferUnitQueueColumnFiltersForUnitType) {
                    onChangeUnitTypeFilters([] as number[]);
                } else {
                    onChangeUnitTypeFilters(updatedValues as number[]);
                }
            },
            dataTestId: `${agencyTypeIdToDisplay[unitType].lowercase}-unit-type-filter`,
        };
    };

    const onOpenChangeHandler = (open: boolean) => {
        if (!open && isFirstResponder) {
            queueStore.saveFrWorkspaceWindow();
        }
    };

    return (
        <Popover hasArrow={false} onOpenChange={onOpenChangeHandler}>
            <PopoverTrigger asChild>
                <IconButton
                    aria-label="filter by unit types"
                    icon="Filter"
                    isActive={isActive}
                    variant={buttonVariant}
                    data-testid="unit-queue-filter-popover-button"
                />
            </PopoverTrigger>
            <PopoverContent align="start" style={{ paddingRight: 0 }}>
                <VStack data-testid="unit-queue-filter-popover-body" pr={0} pb={0}>
                    <HStack justifyContent="flex-end" alignItems="center" w="100%" pr="22px">
                        {showClearAll && (
                            <Button
                                variant="ghost"
                                onClick={clearAll}
                                data-testid="unit-queue-clear-all-button"
                            >
                                Clear All
                            </Button>
                        )}
                    </HStack>
                    <Box
                        pr="12px"
                        minWidth="350px"
                        maxWidth="350px"
                        maxHeight="500px"
                        overflowY="auto"
                        textTransform="none"
                        className="custom-scrollbar"
                    >
                        {filterComponentsMap.map((filter) => (
                            <QueueFilterMultiselect
                                key={filter.label}
                                label={filter.label}
                                attributeOptions={filter.attributeOptions}
                                values={filter.values}
                                onChange={(values) => filter.onChange(values as number[])}
                                data-testid={`unit-queue-filter-${filter.label
                                    .replace(/\s+/g, '-')
                                    .toLowerCase()}`}
                            />
                        ))}
                        {isQueueType(globalAttributes.agencyTypeGlobal.EMS) && (
                            <QueueFilterMultiselect
                                {...getUnitTypePropsForType(globalAttributes.agencyTypeGlobal.EMS)}
                            />
                        )}
                        {isQueueType(globalAttributes.agencyTypeGlobal.FIRE) && (
                            <QueueFilterMultiselect
                                {...getUnitTypePropsForType(globalAttributes.agencyTypeGlobal.FIRE)}
                            />
                        )}
                        {isQueueType(globalAttributes.agencyTypeGlobal.POLICE) && (
                            <QueueFilterMultiselect
                                {...getUnitTypePropsForType(
                                    globalAttributes.agencyTypeGlobal.POLICE
                                )}
                            />
                        )}
                    </Box>
                </VStack>
            </PopoverContent>
        </Popover>
    );
});
